import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Clock, Navigation, StopCircle, Building2, Users } from 'lucide-react';
import StartVisit from './StartVisit';
import EndVisit from './EndVisit';
import AddSupplierToVisit from './AddSupplierToVisit';
import JoinVisit from './JoinVisit';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MyVisits() {
  const [visits, setVisits] = useState([]);
  const [showStart, setShowStart] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [endingVisit, setEndingVisit] = useState(null);
  const [addingSupplierTo, setAddingSupplierTo] = useState(null);

  useEffect(() => {
    fetchVisits();
  }, []);

  const fetchVisits = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/visits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setVisits(data);
    } catch (err) {
      console.error('Failed to fetch visits:', err);
    }
  };

  const statusColor = (s) => {
    if (s === 'ongoing') return 'bg-orange-100 text-orange-700';
    if (s === 'completed_pending_approval') return 'bg-yellow-100 text-yellow-700';
    if (s === 'approved') return 'bg-green-100 text-green-700';
    if (s === 'rejected') return 'bg-red-100 text-red-700';
    if (s === 'completed') return 'bg-blue-100 text-blue-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-xl font-bold">My Visits</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowJoin(true)}
            className="flex items-center space-x-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            data-testid="join-visit-btn"
          >
            <Users size={18} />
            <span className="text-sm font-semibold">Join</span>
          </button>
          <button
            onClick={() => setShowStart(true)}
            className="flex items-center space-x-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            data-testid="new-visit-btn"
          >
            <Plus size={18} />
            <span className="text-sm font-semibold">New</span>
          </button>
        </div>
      </div>

      {showStart && <StartVisit onClose={() => { setShowStart(false); fetchVisits(); }} />}
      {showJoin && <JoinVisit onClose={() => { setShowJoin(false); fetchVisits(); }} />}
      {endingVisit && <EndVisit visit={endingVisit} onClose={() => { setEndingVisit(null); fetchVisits(); }} />}
      {addingSupplierTo && (
        <AddSupplierToVisit
          visit={addingSupplierTo}
          onClose={() => { setAddingSupplierTo(null); fetchVisits(); }}
        />
      )}

      <div className="space-y-3">
        {visits.map(visit => (
          <div key={visit.id} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200" data-testid={`visit-card-${visit.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-gray-900">{visit.supplier_name}</h3>
                  {visit.is_joined && (
                    <span
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-purple-100 text-purple-700 text-[10px] font-semibold rounded-full"
                      data-testid={`joined-badge-${visit.id}`}
                    >
                      <Users size={10} /> Joined
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">{visit.purpose}</p>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${statusColor(visit.status)}`}>
                {visit.status?.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex items-center space-x-1 text-gray-600">
                <Clock size={14} />
                <span>{visit.duration_hours ? `${visit.duration_hours}h` : '-'}</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-600">
                <Navigation size={14} />
                <span>{visit.distance_km ? `${visit.distance_km} km` : '-'}</span>
              </div>
              <div className="flex items-center space-x-1 text-gray-600">
                <MapPin size={14} />
                <span>{new Date(visit.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {visit.status === 'ongoing' && (
              <div className={`mt-3 grid ${visit.is_joined ? 'grid-cols-1' : 'grid-cols-2'} gap-2`}>
                {!visit.is_joined && (
                  <button
                    onClick={() => setAddingSupplierTo(visit)}
                    className="flex items-center justify-center space-x-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold border border-blue-200"
                    data-testid={`add-supplier-btn-${visit.id}`}
                  >
                    <Building2 size={16} />
                    <span>+ Supplier ({(visit.supplier_ids || [visit.supplier_id]).length})</span>
                  </button>
                )}
                <button
                  onClick={() => setEndingVisit(visit)}
                  className="flex items-center justify-center space-x-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold"
                  data-testid={`end-visit-btn-${visit.id}`}
                >
                  <StopCircle size={16} />
                  <span>End Visit</span>
                </button>
              </div>
            )}

            {visit.expense?.amount > 0 && (
              <div className="mt-2 text-xs text-gray-700">
                Expense: ₹{visit.expense.amount} ({visit.expense.status})
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
